<template>
  <div class="date_range_picker" @click.stop>
    <div v-if="timezone" class="date_picker_timezone">
      <span>({{timezone}}) {{timezoneName}}</span>
    </div>

    <div class="calendar compare">
      <month-calendar
                      :monthStr="tempMonthStr"
                      :minDate="minDate"
                      :maxDate="computedMaxDate"
                      :minSelectableDate="minSelectableDate"
                      :dateRange="tempDateRange"
                      :analysis-range="analysisRange"
                      :previewEndDate="previewEndDate"
                      :highlight="null"
                      :unpaid="null"
                      @onMonthAdd="onMonthAdd"
                      @onDateClick="onDateClick"
                      @onRangePreviewChange="onRangePreviewChange"/>
    </div>
    <div class="clearfix">
      <p v-if="isSameAnalysis" class="notice">
        <span v-html="$t('mine.calendar.warning.isSameDate')"></span>
      </p>
      <p class="action">
        <ui-button @click.stop="onCancelClick">
          {{ $t('common.cancel') }}
        </ui-button>
        <ui-button primary
          :disabled="!canApply || isSameAnalysis"
          @click.stop="onConfirmClick">
          {{ $t('common.confirm') }}
        </ui-button>
      </p>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { format, parse, addMonths, isBefore, addDays, isSameDay } from 'date-fns'
import { getLocaleDateStr, getOffset } from '@/libs/utils'
import MonthCalendar from '@/components/ui/ba/MonthCalendar'
import UiButton from '@/components/ui/UiButton'

export default {
  name: 'CompareDatePicker',
  components: {
    MonthCalendar,
    UiButton
  },
  props: {
    pickerType: {
      type: String,
      default: 'journeyPeriod'
    },
    selected: {
      type: String,
      default: 'now'
    },
    timezoneName: {
      type: String,
      required: false
    },
    minDate: {
      type: Date,
      required: false
    },
    minSelectableDate: {
      type: Date,
      required: false
    },
    maxDate: {
      type: Date,
      required: false
    },
    dateRange: {
      type: Object,
      default () {
        return {
          startDate: null,
          endDate: null
        }
      }
    },
    analysisRange: {
      type: Object,
      default () {
        return {
          startDate: null,
          endDate: null
        }
      },
      required: true
    },
    monthStr: {
      type: String,
      required: false
    },
    maxDays: {
      type: Number,
      default: 0
    },
    excludeTodayBefore: {
      default: false
    }
  },
  data () {
    return {
      tempMonthStr: null,
      tempDateRange: null,
      previewEndDate: null
    }
  },
  computed: {
    ...mapState([
      'planData',
      'domain',
      'isSampling'
    ]),
    timezone () {
      return getOffset(this.timezoneName)
    },
    isSameAnalysis () {
      return isSameDay(this.analysisRange.startDate, this.tempDateRange.startDate) && isSameDay(this.analysisRange.endDate, this.tempDateRange.endDate)
    },
    analysisDateStr () {
      return {
        start: getLocaleDateStr(this.analysisRange.startDate),
        end: getLocaleDateStr(this.analysisRange.endDate)
      }
    },
    startDateStr () {
      if (this.tempDateRange.startDate) {
        return getLocaleDateStr(this.tempDateRange.startDate)
      } else {
        return this.$t('common.placeHolder.startDate')
      }
    },
    endDateStr () {
      if (this.tempDateRange.endDate) {
        return getLocaleDateStr(this.tempDateRange.endDate)
      } else {
        return this.$t('common.placeHolder.endDate')
      }
    },
    canApply () {
      return this.tempDateRange.startDate && this.tempDateRange.endDate
    },
    computedMinDate () {
      return this.minDate
    },
    computedMaxDate () {
      if (this.maxDays && this.tempDateRange.startDate && !this.tempDateRange.endDate) {
        if (this.isStartDateBefore2022) {
          return new Date('2021-12-31')
        }

        if (this.excludeTodayBefore) {
          const today = new Date()
          const yesterday = addDays(today, -1)
          const maginotDate = addDays(today, -this.excludeTodayBefore)

          if (isBefore(yesterday, this.maxDate) && isBefore(this.tempDateRange.startDate, maginotDate)) {
            return yesterday
          }
        }

        const newEndDate = addDays(this.tempDateRange.startDate, this.maxDays - 1)

        if (!this.maxDate || isBefore(newEndDate, this.maxDate)) {
          return newEndDate
        }
      }

      return this.maxDate
    }
  },
  created () {
    if (this.monthStr) {
      this.tempMonthStr = this.monthStr
    } else {
      const range = this.dateRange

      if (range && range.startDate && range.endDate) {
        this.tempMonthStr = format(range.endDate, 'YYYY-MM')
      } else if (this.analysisRange) {
        this.tempMonthStr = format(this.analysisRange.endDate, 'YYYY-MM')
      } else {
        this.tempMonthStr = format(new Date(), 'YYYY-MM')
      }
    }
    this.setTempDateRange()

    this.closeFn = () => {
      this.onCancelClick()
    }
    document.body.addEventListener('click', this.closeFn, false)
  },
  destroyed () {
    document.body.removeEventListener('click', this.closeFn)
  },
  methods: {
    setTempDateRange () {
      this.tempDateRange = { ...this.dateRange }
    },
    onMonthAdd (months) {
      let monthDate = parse(this.tempMonthStr)
      monthDate = addMonths(monthDate, months)
      this.tempMonthStr = format(monthDate, 'YYYY-MM-DD')
    },
    onRangePreviewChange (type, date) {
      this.previewEndDate = date
    },
    onDateClick (date, type) {
      const range = this.tempDateRange

      if (!range.startDate || range.endDate || (!range.endDate && isBefore(date, range.startDate))) {
        range.startDate = date
        range.endDate = null
        this.previewEndDate = null
      } else {
        range.endDate = date
        this.previewEndDate = null
      }
    },
    onCancelClick () {
      this.$emit('onRangeCancel')
    },
    onConfirmClick () {
      const range = this.tempDateRange

      if (!range.startDate || !range.endDate) {
        return
      }
      this.$emit('onRangeChange', {
        range: this.tempDateRange,
        monthStr: this.tempMonthStr
      })
    }
  }
}
</script>

<style lang="scss" scoped>
$analysisColor: #0085FF;
.date_range_picker {
  top: 0;
  padding: 25px;
  transform: translate(0);
  width: 289px;

  .action {
    margin-top: 8.5px;
    button {
      width: initial;
      height: 34px;
    }
  }

  .calendar {

    & .month_calendar {
      padding: 0 0 8px 0;
      width: 239px;
      flex-shrink: initial;

      & td {
        & > span {
          font-size: 13px;
          width: 15px;
          height: 15px;
          font-weight: 400;
        }
      }

      .month_control {
        height: 30px;
        display: flex;
        flex-grow: 0;
        justify-content: stretch;
        margin-bottom: 10px;

        & .month_label {
          display: inline;
          width: calc(100% - 60px);
        }

        & > button {
          position: relative;
          width: 30px;
          top: initial;
          left: initial;
          transform: initial;
          margin-top: initial;
        }
      }
    }
  }
  .date_picker_timezone {
    margin-bottom: 20px;
    height: 14px;

    span {
      display: inline-block;
      vertical-align: middle;
      font-weight: 400 !important;
      font-size: 12px;
    }

    .icon {
      vertical-align: middle;
    }
  }

  .date_range_picker--wrap {
    border-bottom: 1px dashed #d7d7d7;
    padding-bottom: 20px;

    .date_range_picker_label--wrap {
      border: 2px solid transparent;
      display: flex;
      align-items: center;

      .date_range_picker_label {
        display: inline-block;
        line-height: 38px;
        padding: 0 5px;
        font-size: 14px;
        font-weight: 500;
        width: 150px;
        .icon {
          vertical-align: middle;
          margin-right: 10px;
        }
      }

      .date_range_picker_input--wrap {
        flex-grow: 1;
        display: flex;
        align-items: center;
        font-size: 14px;
        .date_range_picker_input {
          input {
            width: 160px;
          }
        }
        .date_range_picker_analysis {
          width: 160px;
          font-size: 14px;
          padding: 9px 10px 9px 38px;
          color: $analysisColor;
          box-sizing: border-box;
        }
      }
    }
  }

  .plan-legend {
    border-bottom: 1px dashed #d7d7d7;
    padding: 26px 0 20px;
    text-align: center;
    margin-bottom: 3px;

    .legend {
      font-size: 12px;
      color: #888888;
      margin: 0 13px;

      &::before {
        content: "";
        display: inline-block;
        vertical-align: middle;
        width: 8px;
        height: 8px;
        background-color: #ddd;
        border-radius: 5px;
        margin: 0 7px 2px 0;
      }

      &.everyone {
        &::before {
          background-color: #333333;
        }

        & /deep/ span {
          color: #333333;
        }
      }

      &.pro {
        &::before {
          background-color: #FF2D92;
        }

        & /deep/ span {
          color: #FF2D92;
        }
      }
    }
  }
  // height: 16px;
  font-size: 12px;
  line-height: 1.33;
  color: #444;

  .notice {
    margin: 0 0 8px 0;
  }
}
.notice span {
  color: #EC0047;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
}
</style>
